// Minor lines: the tier a city puts its buses in (PageLine.minor), or any
// line that would crowd the map zoomed out. The one place their zoom
// thresholds and styling live; layers.ts draws their lines, render.ts their
// vehicles and their stops, and the shell folds their groups in the line
// list.
//
// Zoomed out, a minor line and its vehicles aren't drawn at all, so the
// city's rail network reads on its own. From LINE_FROM they fade in as thin,
// muted lines under every major line, fully there by LINE_FULL. From
// VEHICLE_Z (street level) the vehicles appear as small dots, and from
// BULLET_Z as a tag with their route's number, in type sized to be read
// (LABEL_FONT) rather than to fit a train's disc; a tag that would cover one
// already drawn stays a dot, so a busy street doesn't turn into a pile of
// tags. Their stops aren't stations (unless a major line calls there too)
// and don't make a station an interchange: from STOP_Z, as the vehicles'
// numbers appear, they're small muted dots under the stations, which open
// the stop's card like a station's, and from STOP_NAME_Z they're named.
// Zooms are Leaflet's (view.ts: MapLibre's + 1).

import type { LineId } from './types.ts';

export const MINOR = {
  // Lines fade in from LINE_FROM to LINE_FULL.
  LINE_FROM: 12.5,
  LINE_FULL: 13.25,
  // Vehicles from here, as dots.
  VEHICLE_Z: 14,
  // Dots become bullets from here.
  BULLET_Z: 15.5,
  // A minor line's opacity once faded in, and its width as a share of a
  // major line's.
  LINE_OPACITY: 0.55,
  LINE_WIDTH: 0.45,
  // A vehicle dot's radius in px, before the zoom's train scale.
  DOT_RADIUS: 2.6,
  // A tag's type size in px: from LABEL_FONT at BULLET_Z, a px larger each
  // zoom in, to LABEL_FONT_MAX.
  LABEL_FONT: 12,
  LABEL_FONT_MAX: 14,
  // Longer route names (Seattle's "Duvall-Monroe Shuttle") stay dots.
  LABEL_MAX_CHARS: 6,
  // Stops only minor lines serve: dots from STOP_Z, names from STOP_NAME_Z.
  STOP_Z: 15.5,
  STOP_NAME_Z: 17,
} as const;

// The line styles' view of the tier: a record of lines, each maybe minor.
export type MinorLines = Readonly<Partial<Record<LineId, { readonly minor?: boolean }>>>;

export const isMinor = (lines: MinorLines, id: LineId): boolean => lines[id]?.minor === true;
